import { goAnchor } from "./app-nav";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Loader2, Paperclip, Search, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import ChatPane, { initials, type Member, type SendKind } from "./inboxapp-pane";
import { api, apiErrorText } from "@/lib/api";
import { onServerEvent } from "@/lib/events";
import { ROLE_LABELS, useAuth, type Role } from "./auth";
import { MAX_UPLOAD_MB, inboxArchive, setInboxArchive } from "./inbox-api";
import { snippetsApi, type Snippet } from "./snippets-api";
import { inboxRulesApi, type InboxRules } from "./inbox-rules-api";
import { stagesApi, type Stage } from "./contacts-api";
import { channelsApi, conversationsApi, listTime, takeConversation, takeNewDialog, takeNewDialogBack, type Channel, type Conversation, type ConversationFull, type Folders, type Message } from "./inbox-api";
import { inboxAppAgentRoles, inboxAppAgentSteps, inboxAppEmptyStates, inboxAppHints, inboxAppNav, inboxAppSorts } from "@/mocks/inboxapp";
import { t, tn } from "@/lib/i18n";

/* Окно «Входящие» на данных сервера: папки (все / мои / совместные / нераспределённые), этапы жизненного цикла
   и команды как папки со счётчиками, список диалогов с поиском и сортировкой, переписка справа.
   Новые сообщения и смены статусов приходят живыми событиями (SSE). Агент видит только своё — это решает сервер. */

const RAIL = [
  { id: "inbox", icon: "ri-inbox-line", label: t("Входящие"), anchor: "#inbox-app", active: true },
  { id: "dashboard", icon: "ri-dashboard-line", label: t("Дашборд"), anchor: "#dashboard", active: false },
  { id: "contacts", icon: "ri-contacts-book-2-line", label: t("Контакты"), anchor: "#contacts-app", active: false },
  { id: "channels", icon: "ri-share-forward-line", label: t("Каналы"), anchor: "#channels", active: false },
  { id: "ai", icon: "ri-sparkling-line", label: "AI Assist", anchor: "#ai-assist", active: false },
  { id: "ai-agents", icon: "ri-robot-2-line", label: t("AI-агенты"), anchor: "#ai-agents", active: false },
  { id: "broadcasts", icon: "ri-mail-send-line", label: t("Рассылки"), anchor: "#broadcasts", active: false },
  { id: "workflows", icon: "ri-flow-chart", label: t("Рабочие процессы"), anchor: "#workflows", active: false },
  { id: "reports", icon: "ri-bar-chart-2-line", label: t("Отчеты"), anchor: "#reports", active: false },
  { id: "lifecycle", icon: "ri-refresh-line", label: t("Жизненный цикл"), anchor: "#lifecycle", active: false },
  { id: "settings", icon: "ri-settings-3-line", label: t("Настройки"), anchor: "#settings", active: false },
  { id: "general", icon: "ri-information-line", label: t("Общие сведения"), anchor: "#general-settings", active: false },
];

type Empty = { title: string; text: string; action: string; target: string };
const EMPTY = inboxAppEmptyStates as unknown as Record<string, Empty>;
const PAGE = 50;

const channelIcon = (c: string) => (c === "telegram" ? "ri-telegram-line" : c === "email" ? "ri-mail-line" : c === "instagram" ? "ri-instagram-line" : c === "wazzup" ? "ri-archive-line" : "ri-whatsapp-line");

/** иллюстрация пустого состояния: два «пузыря» переписки, без SVG и картинок */
function EmptyArt() {
  return (
    <div className="relative mx-auto h-32 w-48" aria-hidden>
      <div className="absolute start-1 top-2 flex h-20 w-28 flex-col justify-center gap-1.5 rounded-xl bg-foreground/[.07] px-3">
        <span className="h-1.5 w-16 rounded-full bg-foreground/15" />
        <span className="h-1.5 w-20 rounded-full bg-foreground/10" />
        <span className="h-1.5 w-12 rounded-full bg-foreground/10" />
      </div>
      <div className="absolute bottom-1 end-1 grid h-24 w-32 place-items-center rounded-xl bg-foreground/[.05]">
        <span className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <span key={i} className="size-2.5 rounded-full bg-foreground/15" />
          ))}
        </span>
      </div>
    </div>
  );
}

function EmptyState({ empty, extra, onAction }: { empty: Empty; extra?: string; onAction: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-5 py-10 text-center">
      <EmptyArt />
      <h4 className="mt-1 text-[15px] font-semibold leading-snug">{empty.title}</h4>
      <p className="max-w-sm text-[12px] leading-relaxed text-muted-foreground">{empty.text}</p>
      <button
        type="button"
        onClick={onAction}
        className="mt-1.5 inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-4 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110"
      >
        <i className="ri-add-line text-[14px] leading-none" aria-hidden />
        {empty.action}
      </button>
      {extra ? <p className="mt-1 text-[10.5px] text-muted-foreground">{extra}</p> : null}
    </div>
  );
}

function FolderRow({ active, icon, label, count, chip, onClick }: { active: boolean; icon: string; label: string; count?: number; chip?: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex w-full cursor-pointer items-center gap-2 rounded-btn px-2 py-[7px] text-start text-[12px] font-medium transition",
        active ? "bg-primary/15 text-primary" : "text-foreground/75 hover:bg-muted hover:text-foreground",
      )}
    >
      {chip ? (
        <span className={cn("grid size-4 flex-none place-items-center rounded-[4px] text-[10px] leading-none", chip)} aria-hidden>
          <i className={icon} />
        </span>
      ) : (
        <span className={cn("grid size-5 flex-none place-items-center text-[13px] leading-none", active ? "text-primary" : "text-muted-foreground")} aria-hidden>
          <i className={icon} />
        </span>
      )}
      <span className={cn("min-w-0 flex-1 truncate", chip && "text-[11.5px]")}>{label}</span>
      {typeof count === "number" && count > 0 ? (
        <span className={cn("flex-none tabular-nums text-[10.5px]", active ? "text-primary" : "text-muted-foreground/80")}>{count}</span>
      ) : null}
    </button>
  );
}

function GroupHead({ label, open, count, onToggle }: { label: string; open: boolean; count?: number; onToggle: () => void }) {
  return (
    <div className="mt-2 flex items-center gap-1 px-1">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 text-start">
        <i className={cn("ri-arrow-down-s-line text-[13px] leading-none text-muted-foreground transition", !open && "-rotate-90")} aria-hidden />
        <span className="truncate text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</span>
        {typeof count === "number" ? <span className="tabular-nums text-[10px] text-muted-foreground/70">{count}</span> : null}
      </button>
    </div>
  );
}

const stageChip = (s: Stage) => (s.isWon ? "bg-primary/15 text-primary" : s.lost ? "bg-muted text-muted-foreground" : s.isDefault ? "bg-muted text-muted-foreground" : "bg-amber-400/20 text-amber-600");

const SORT_API: Record<string, "new" | "old" | "unread"> = { "open-new": "new", "open-old": "old", unread: "unread" };

function InboxAppDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const { user } = useAuth();
  const me = user?.id ?? "";
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [rules, setRules] = useState<InboxRules | null>(null);
  useEffect(() => {
    snippetsApi
      .list()
      .then((r) => setSnippets(r.items))
      .catch(() => undefined);
    inboxRulesApi.get().then(setRules).catch(() => undefined);
  }, []);
  /** диалог ждёт ответа дольше, чем задано в правилах («Напоминание о диалоге без ответа») */
  const overdue = (d: Conversation) =>
    d.waiting && d.status !== "closed" && Boolean(d.lastMessageAt) && Date.now() - new Date(d.lastMessageAt as string).getTime() >= (rules?.remindHours ?? 8) * 3600_000;
  const staff = user?.role === "owner" || user?.role === "admin" || user?.role === "manager";
  const readOnly = user?.role === "viewer";

  const [stages, setStages] = useState<Stage[]>([]);
  const [teams, setTeams] = useState<{ id: string; name: string }[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [folders, setFolders] = useState<Folders | null>(null);
  const [folder, setFolder] = useState("all");
  const [archive, setArchive] = useState(() => inboxArchive(user?.role)); // закрытые тоже в списке и счётчиках
  const [assignee, setAssignee] = useState(""); // фильтр по менеджеру: "" все, "none" без ответственного, id сотрудника
  const [tab, setTab] = useState<"chats" | "calls">("chats");
  const [sort, setSort] = useState("open-new");
  const [unreplied, setUnreplied] = useState(false);
  const [query, setQuery] = useState("");
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [list, setList] = useState<Conversation[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(takeConversation);
  const [active, setActive] = useState<ConversationFull | null>(null);
  const [busy, setBusy] = useState(false);
  const [openGroups, setOpenGroups] = useState({ lifecycle: true, team: true, linked: true });
  const newBack = useRef<(() => void) | null>(null); // окно открыто из карточки клиента: «Отмена» возвращает в карточку
  const [pendingPhoneRef] = useState(() => {
    newBack.current = takeNewDialogBack();
    return takeNewDialog() ?? "";
  });
  const [modal, setModal] = useState<null | "agent" | "new">(() => (pendingPhoneRef ? "new" : null));
  const [agentRole, setAgentRole] = useState(inboxAppAgentRoles[0]);
  const [newPhone, setNewPhone] = useState(() => pendingPhoneRef);
  const [newChannel, setNewChannel] = useState("");
  const [newSubject, setNewSubject] = useState(""); // письмо: тема отдельной строкой (уходит первой строкой текста)
  const [newText, setNewText] = useState("");
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [newBusy, setNewBusy] = useState(false);
  const newFileInput = useRef<HTMLInputElement>(null);
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = useCallback((text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  }, []);

  const jump = (target: string) => {
    const anchor = target === "catalog" ? "#catalog" : "#channels";
    if (!goAnchor(anchor)) flash(t("Раздел пока не открывается"));
  };

  // поиск — с задержкой, чтобы не дёргать сервер на каждую букву
  useEffect(() => {
    const t = setTimeout(() => setQ(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // справочники
  useEffect(() => {
    stagesApi.list().then(setStages).catch(() => undefined);
    api<{ id: string; name: string }[]>("/teams").then(setTeams).catch(() => undefined);
    api<{ id: string; name: string; email: string; role: Role; status: string }[]>("/users")
      .then((rows) => setMembers(rows.filter((u) => u.status !== "disabled" && u.role !== "viewer").map((u) => ({ id: u.id, name: u.name || u.email, role: ROLE_LABELS[u.role] ?? u.role }))))
      .catch(() => undefined);
    channelsApi
      .list()
      .then((r) => setChannels(r.items))
      .catch(() => undefined);
  }, []);

  const loadFolders = useCallback(() => conversationsApi.folders(archive).then(setFolders).catch(() => undefined), [archive]);

  const loadList = useCallback(
    async (append = false) => {
      if (!append) setLoading(true);
      try {
        const r = await conversationsApi.list({ folder, q, sort: SORT_API[sort] ?? "new", waiting: unreplied, assignee, archive, limit: PAGE, offset: append ? list.length : 0 });
        setList((prev) => (append ? [...prev, ...r.items] : r.items));
        setTotal(r.total);
      } catch (err) {
        flash(apiErrorText(err));
      } finally {
        setLoading(false);
      }
    },
    [folder, q, sort, unreplied, assignee, archive, list.length, flash],
  );

  useEffect(() => {
    void loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    void loadList(false);
  }, [folder, q, sort, unreplied, assignee, archive]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleArchive = () => {
    setInboxArchive(!archive);
    setArchive(!archive);
    setActiveId(null);
  };

  const openConversation = useCallback(
    async (id: string, markRead = true) => {
      try {
        const c = await conversationsApi.get(id);
        setActive(c);
        if (markRead && c.unread > 0 && !readOnly) {
          conversationsApi.read(id).catch(() => undefined);
          setList((prev) => prev.map((x) => (x.id === id ? { ...x, unread: 0 } : x)));
          setActive((prev) => (prev && prev.id === id ? { ...prev, unread: 0 } : prev));
        }
      } catch (err) {
        flash(apiErrorText(err));
        setActiveId(null);
      }
    },
    [flash, readOnly],
  );

  useEffect(() => {
    if (activeId) void openConversation(activeId);
    else setActive(null);
  }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // живые события: новые сообщения, изменения диалогов, состояние каналов
  const scheduleRefresh = useCallback(() => {
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      void loadFolders();
      void loadList(false);
    }, 400);
  }, [loadFolders, loadList]);

  useEffect(() => {
    const offMsg = onServerEvent("message.new", (data) => {
      const d = data as { conversationId: string; message: Message };
      if (!d?.message) return;
      setActive((prev) => {
        if (!prev || prev.id !== d.conversationId) return prev;
        const i = prev.messages.findIndex((m) => m.id === d.message.id);
        const messages = i >= 0 ? prev.messages.map((m, k) => (k === i ? d.message : m)) : [...prev.messages, d.message];
        return { ...prev, messages };
      });
      if (d.message.kind === "in" && activeId === d.conversationId && !readOnly) conversationsApi.read(d.conversationId).catch(() => undefined);
      scheduleRefresh();
    });
    const offConv = onServerEvent("conversation.updated", (data) => {
      const d = data as { id: string };
      if (d?.id && d.id === activeId) {
        conversationsApi
          .get(d.id)
          .then((c) => setActive((prev) => (prev && prev.id === c.id ? { ...c, messages: prev.messages.length > c.messages.length ? prev.messages : c.messages } : c)))
          .catch(() => undefined);
      }
      scheduleRefresh();
    });
    const offCh = onServerEvent("channel.state", () => {
      channelsApi
        .list()
        .then((r) => setChannels(r.items))
        .catch(() => undefined);
    });
    return () => {
      offMsg();
      offConv();
      offCh();
      clearTimeout(refreshTimer.current);
    };
  }, [activeId, readOnly, scheduleRefresh]);

  const connected = channels.filter((c) => c.status === "connected");
  const counts = folders ?? { all: 0, mine: 0, unassigned: 0, bot: 0, collaborations: 0, waiting: 0, unread: 0, closed: 0, linked: 0, unlinked: 0, company: 0, assignees: {}, stages: {}, teams: {} };
  const folderCount = (id: string) => (id === "incoming-calls" ? 0 : ((counts as unknown as Record<string, number>)[id] ?? 0));

  const folderLabel = useMemo(() => {
    if (folder.startsWith("stage:")) return stages.find((s) => s.id === folder.slice(6))?.name ?? t("Этап");
    if (folder.startsWith("team:")) return teams.find((x) => x.id === folder.slice(5))?.name ?? t("Команда");
    if (folder === "closed") return t("Закрытые");
    if (folder === "bot") return t("Ведёт бот");
    if (folder === "linked") return t("С контактом");
    if (folder === "unlinked") return t("Без контакта");
    if (folder === "company") return t("С компанией");
    return inboxAppNav.find((f) => f.id === folder)?.label ?? t("Все");
  }, [folder, stages, teams]);

  const empty: Empty = useMemo(() => {
    if (folder.startsWith("stage:")) return { title: t("На этапе «{name}» пусто", { name: folderLabel }), text: t("Сюда попадут диалоги с контактами на этом этапе жизненного цикла."), action: t("Подключить канал"), target: "channels" };
    if (folder.startsWith("team:")) return { title: t("У команды «{name}» нет диалогов", { name: folderLabel }), text: t("Диалоги попадают сюда, когда их назначают команде или контакт закреплён за ней."), action: t("Подключить канал"), target: "channels" };
    if (folder === "bot") return { title: t("Бот сейчас никого не ведёт"), text: t("Сюда попадают диалоги без ответственного в каналах, где с телефона отвечает бот. Имя бота задаётся в «Каналах»."), action: t("Подключить канал"), target: "channels" };
    if (folder === "closed") return { title: t("Закрытых диалогов нет"), text: t("Закрытые диалоги остаются здесь: история, заметки и вложения не теряются."), action: t("Подключить канал"), target: "channels" };
    return EMPTY[folder] ?? EMPTY.all;
  }, [folder, folderLabel]);

  const pickFolder = (id: string) => {
    if (id === "ai-agent") {
      setModal("agent");
      return;
    }
    setFolder(id);
    setActiveId(null);
    setTab(id === "incoming-calls" ? "calls" : "chats");
  };

  const patchActive = (c: Conversation) => {
    setActive((prev) => (prev && prev.id === c.id ? { ...prev, ...c } : prev));
    setList((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...c } : x)));
  };

  const sendMessage = async (text: string, kind: SendKind, replyTo?: string | null, snippetId?: string | null) => {
    if (!active) return;
    setBusy(true);
    try {
      const m = await conversationsApi.send(active.id, text, kind, replyTo, snippetId);
      const batch = [m, ...(m.also ?? [])]; // по шаблону файлы уходят отдельными сообщениями
      setActive((prev) => (prev && prev.id === m.conversationId ? { ...prev, messages: [...prev.messages, ...batch.filter((x) => !prev.messages.some((y) => y.id === x.id))] } : prev));
      if (m.status === "failed") flash(t("Сообщение не отправлено: {error}", { error: apiErrorText({ status: 0, message: m.error ?? "" } as never) }));
      else if (kind === "note") flash(t("Заметка добавлена — клиент её не увидит"));
      void loadFolders();
    } catch (err) {
      flash(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadFile = async (file: File, caption: string) => {
    if (!active) return;
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      flash(t("Файл «{name}» больше {n} МБ — отправьте ссылкой или сожмите.", { name: file.name, n: MAX_UPLOAD_MB }));
      return;
    }
    setBusy(true);
    try {
      const m = await conversationsApi.upload(active.id, file, caption);
      setActive((prev) => (prev && prev.id === m.conversationId ? { ...prev, messages: prev.messages.some((x) => x.id === m.id) ? prev.messages : [...prev.messages, m] } : prev));
      if (m.status === "failed") flash(t("Файл не отправлен — проверьте подключение канала."));
    } catch (err) {
      flash(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const forwardMessage = async (messageId: number, toId: string, toName: string) => {
    if (!active) return;
    setBusy(true);
    try {
      const r = await conversationsApi.forward(active.id, messageId, [toId]);
      const m = r.items[0];
      if (m?.status === "failed") flash(t("Не удалось переслать в «{name}» — проверьте подключение канала.", { name: toName }));
      else flash(t("Переслано в «{name}»", { name: toName }));
      if (toId === active.id && m) setActive((prev) => (prev && prev.id === m.conversationId && !prev.messages.some((x) => x.id === m.id) ? { ...prev, messages: [...prev.messages, m] } : prev));
      void loadList(false);
    } catch (err) {
      flash(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const assign = async (userId: string | null) => {
    if (!active) return;
    try {
      patchActive(await conversationsApi.update(active.id, { assigneeId: userId }));
      flash(userId ? t("Ответственный назначен") : t("Диалог снят с ответственного"));
      void loadFolders();
    } catch (err) {
      flash(apiErrorText(err));
    }
  };

  const setCategory = async (category: string) => {
    if (!active) return;
    try {
      patchActive(await conversationsApi.update(active.id, { category }));
    } catch (err) {
      flash(apiErrorText(err));
    }
  };

  const setStatus = async (status: "open" | "closed", note?: string) => {
    if (!active) return;
    try {
      patchActive(await conversationsApi.update(active.id, note ? { status, note } : { status }));
      flash(status === "closed" ? t("Диалог закрыт — он останется в папке «Закрытые»") : t("Диалог открыт снова"));
      void loadFolders();
      void loadList(false);
    } catch (err) {
      flash(apiErrorText(err));
    }
  };

  const loadMore = async () => {
    if (!active || !active.hasMore || !active.messages.length) return;
    try {
      const older = await conversationsApi.get(active.id, active.messages[0].id);
      setActive((prev) => (prev && prev.id === older.id ? { ...prev, messages: [...older.messages, ...prev.messages], hasMore: older.hasMore } : prev));
    } catch {
      /* подгрузим при следующей прокрутке */
    }
  };

  // «Новый диалог»: WhatsApp — по телефону, почта — по адресу; канал по умолчанию подбирается под адресата
  const waChannels = connected.filter((c) => c.kind === "whatsapp");
  const mailChannels = connected.filter((c) => c.kind === "email");
  const writable = [...waChannels, ...mailChannels];
  const toIsEmail = newPhone.includes("@");
  const newTarget = writable.find((c) => c.id === newChannel) ?? (toIsEmail ? mailChannels[0] : waChannels[0]) ?? writable[0];
  const newIsMail = newTarget?.kind === "email";
  const newValid = newIsMail ? /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newPhone.trim()) : newPhone.replace(/\D/g, "").length >= 8;

  /* stay — остаться во «Входящих» (отправили сообщение или идём в «Каналы»); иначе из карточки клиента возвращаемся обратно */
  const closeNew = (stay = false) => {
    setModal(null);
    setNewPhone("");
    setNewSubject("");
    setNewText("");
    setNewFiles([]);
    const back = newBack.current;
    newBack.current = null;
    if (!stay && back) back();
  };

  // текст первого сообщения: у письма тема — первая строка (так сервер ставит Subject)
  const newBody = [newIsMail ? newSubject.trim() : "", newText.trim()].filter(Boolean).join("\n\n");
  const newHasMessage = Boolean(newBody) || newFiles.length > 0;

  /* «Открыть диалог» — пустой диалог; с текстом или файлами — сразу отправляем первое сообщение (письмо с вложениями) */
  const createNew = async () => {
    const chId = newTarget?.id;
    if (!chId) {
      flash(t("Сначала подключите WhatsApp или почту в разделе «Каналы»"));
      return;
    }
    if (!newValid || newBusy) return;
    setNewBusy(true);
    try {
      const c = await conversationsApi.create(newIsMail ? { channelId: chId, email: newPhone.trim() } : { channelId: chId, phone: newPhone });
      let sent = false;
      try {
        if (newFiles.length) {
          for (const [i, f] of newFiles.entries()) await conversationsApi.upload(c.id, f, i === 0 ? newBody : "");
          sent = true;
        } else if (newBody) {
          await conversationsApi.send(c.id, newBody);
          sent = true;
        }
      } catch (err) {
        flash(apiErrorText(err));
      }
      closeNew(true);
      setFolder("mine");
      setActiveId(c.id);
      void loadFolders();
      if (sent) flash(newIsMail ? t("Письмо отправлено") : t("Сообщение отправлено"));
      else if (!newHasMessage) flash(t("Диалог открыт — напишите первое сообщение"));
    } catch (err) {
      flash(apiErrorText(err));
    } finally {
      setNewBusy(false);
    }
  };

  const createAgent = () => {
    setModal(null);
    flash(t("AI-агент «{role}» появится на этапе AI — пока он не создаётся", { role: agentRole.toLowerCase() }));
  };

  const headerLine = connected.length
    ? `${tn(counts.all, "диалог", "диалога", "диалогов")} · ${tn(connected.length, "канал", "канала", "каналов")}`
    : channels.length
      ? t("канал не подключён")
      : t("каналы не подключены");

  return (
    <Band id="inbox-app" tone="default">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">{str(brand) || "Hub"} / {t("входящие")}</span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className={cn("size-1.5 rounded-full", connected.length ? "bg-emerald-500" : "bg-foreground/30")} aria-hidden />
            {headerLine}
          </span>
        </div>

        {channels.some((c) => c.status === "unauthorized" || c.status === "failed") ? (
          <div className="flex flex-wrap items-center gap-2 border-b border-border bg-amber-400/10 px-3.5 py-2 text-[11.5px]">
            <i className="ri-error-warning-line text-[13px] leading-none text-amber-600" aria-hidden />
            <span className="min-w-0 flex-1 leading-relaxed text-foreground/80">
              {t("WhatsApp {names}: сеанс завершён — новые сообщения не приходят.", {
                names: channels
                  .filter((c) => c.status === "unauthorized" || c.status === "failed")
                  .map((c) => c.name || c.phone)
                  .join(", "),
              })}
            </span>
            <button
              type="button"
              onClick={() => jump("channels")}
              className="inline-flex h-6 flex-none cursor-pointer items-center whitespace-nowrap rounded-full bg-primary px-2.5 text-[10.5px] font-semibold text-primary-foreground transition hover:brightness-110"
            >
              {t("Переподключить")}
            </button>
          </div>
        ) : null}

        <div className="flex">
          {/* иконная панель приложения */}
          <div className="hidden w-12 flex-none flex-col items-center gap-1 border-e border-border py-3 lg:flex">
            {RAIL.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => {
                  if (r.id === "inbox") return;
                  if (!goAnchor(r.anchor)) flash(t("Раздел «{name}» пока не открывается", { name: r.label }));
                }}
                title={r.label}
                aria-label={r.label}
                className={cn(
                  "grid size-8 cursor-pointer place-items-center rounded-btn transition",
                  r.active ? "bg-primary/12 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <i className={cn(r.icon, "text-[15px] leading-none")} aria-hidden />
              </button>
            ))}
            <span className="mt-auto grid size-8 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground" aria-hidden>
              {initials(user?.name ?? "")}
            </span>
          </div>

          {/* папки входящих */}
          <div className="hidden w-[15.5rem] flex-none flex-col border-e border-border lg:flex">
            <div className="flex items-center gap-1 px-3 pb-1 pt-3">
              <p className="text-[13px] font-semibold">{t("Входящие")}</p>
              <div className="ms-auto flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => setSearchOpen((v) => !v)}
                  aria-label={t("Поиск по входящим")}
                  title={t("Поиск по входящим")}
                  className={cn("grid size-6 cursor-pointer place-items-center rounded-[5px] transition hover:bg-muted", searchOpen ? "text-primary" : "text-muted-foreground")}
                >
                  <Search className="size-3.5" aria-hidden />
                </button>
                {!readOnly ? (
                  <button
                    type="button"
                    onClick={() => {
                      setNewChannel(connected[0]?.id ?? "");
                      setModal("new");
                    }}
                    aria-label={t("Новый диалог")}
                    title={t("Написать клиенту первым")}
                    className="grid size-6 cursor-pointer place-items-center rounded-[5px] text-muted-foreground transition hover:bg-muted hover:text-primary"
                  >
                    <i className="ri-edit-line text-[13px] leading-none" aria-hidden />
                  </button>
                ) : null}
              </div>
            </div>

            {searchOpen ? (
              <div className="px-2 pb-1">
                <label className="relative block">
                  <span className="sr-only">{t("Поиск по диалогам")}</span>
                  <Search className="pointer-events-none absolute start-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("Имя, компания или телефон")}
                    className="h-8 w-full box-border rounded-btn border border-border bg-background ps-7 pe-2 text-[11.5px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                  />
                </label>
              </div>
            ) : null}

            <div className="flex flex-col gap-0.5 px-2 pt-1">
              {inboxAppNav.map((f) => (
                <FolderRow key={f.id} active={folder === f.id} icon={f.icon} label={f.label} count={f.id === "ai-agent" ? undefined : folderCount(f.id)} onClick={() => pickFolder(f.id)} />
              ))}
              {counts.bot > 0 || folder === "bot" ? <FolderRow active={folder === "bot"} icon="ri-robot-2-line" label={t("Ведёт бот")} count={counts.bot} onClick={() => pickFolder("bot")} /> : null}
              <FolderRow active={folder === "closed"} icon="ri-checkbox-circle-line" label={t("Закрытые")} count={counts.closed} onClick={() => pickFolder("closed")} />
              <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-btn px-2 py-1.5 text-[11.5px] text-foreground/75 hover:bg-muted" title={t("Закрытые диалоги (архив Wazzup, старая почта) тоже в списке и в счётчиках папок и плиток")}>
                <input type="checkbox" checked={archive} onChange={toggleArchive} className="size-3.5 cursor-pointer accent-primary" data-testid="inbox-archive" />
                <span className="font-medium">{t("С архивом")}</span>
                <span className="ms-auto text-[10px] text-muted-foreground">{archive ? t("все диалоги") : t("только открытые")}</span>
              </label>
            </div>

            <GroupHead label={t("Привязка")} open={openGroups.linked} count={counts.linked} onToggle={() => setOpenGroups((g) => ({ ...g, linked: !g.linked }))} />
            {openGroups.linked ? (
              <div className="flex flex-col gap-0.5 px-2">
                <FolderRow active={folder === "linked"} icon="ri-user-follow-line" label={t("С контактом")} count={counts.linked} onClick={() => pickFolder("linked")} />
                <FolderRow active={folder === "company"} icon="ri-building-line" label={t("С компанией")} count={counts.company} onClick={() => pickFolder("company")} />
                <FolderRow active={folder === "unlinked"} icon="ri-user-unfollow-line" label={t("Без контакта")} count={counts.unlinked} onClick={() => pickFolder("unlinked")} />
              </div>
            ) : null}

            <GroupHead
              label={t("Жизненный цикл")}
              open={openGroups.lifecycle}
              count={Object.values(counts.stages).reduce((a, b) => a + b, 0)}
              onToggle={() => setOpenGroups((g) => ({ ...g, lifecycle: !g.lifecycle }))}
            />
            {openGroups.lifecycle ? (
              <div className="flex flex-col gap-0.5 px-2">
                {stages.map((s) => (
                  <FolderRow
                    key={s.id}
                    active={folder === `stage:${s.id}`}
                    icon={s.icon || "ri-user-add-line"}
                    label={s.name}
                    count={counts.stages[s.id] ?? 0}
                    chip={stageChip(s)}
                    onClick={() => pickFolder(`stage:${s.id}`)}
                  />
                ))}
              </div>
            ) : null}

            <GroupHead label={t("Командные входящие")} open={openGroups.team} count={teams.length} onToggle={() => setOpenGroups((g) => ({ ...g, team: !g.team }))} />
            {openGroups.team ? (
              <div className="px-2">
                {teams.length === 0 ? (
                  <p className="px-2 pb-1 text-[10.5px] leading-relaxed text-muted-foreground">{t("Команды создаются в настройках")}</p>
                ) : (
                  teams.map((team) => (
                    <FolderRow key={team.id} active={folder === `team:${team.id}`} icon="ri-team-line" label={team.name} count={counts.teams[team.id] ?? 0} onClick={() => pickFolder(`team:${team.id}`)} />
                  ))
                )}
              </div>
            ) : null}

            {/* каналы */}
            <div className="mt-auto border-t border-border p-2.5">
              <p className="text-[10.5px] font-semibold leading-tight text-muted-foreground">{t("Каналы")}</p>
              {channels.length === 0 ? (
                <button type="button" onClick={() => jump("channels")} className="mt-1 cursor-pointer text-[10.5px] font-semibold text-primary hover:brightness-110">
                  {t("Подключить WhatsApp →")}
                </button>
              ) : (
                <ul className="mt-1 flex flex-col gap-1">
                  {channels.map((c) => (
                    <li key={c.id} className="flex items-center gap-1.5 text-[10.5px]">
                      <span className={cn("size-1.5 flex-none rounded-full", c.status === "connected" ? "bg-emerald-500" : c.status === "archive" ? "bg-muted-foreground/40" : c.status === "connecting" || c.status === "qr" || c.status === "pairing" ? "bg-amber-400" : "bg-red-400")} aria-hidden />
                      <i className={cn(channelIcon(c.kind), "text-[11px] text-muted-foreground")} aria-hidden />
                      <span className="min-w-0 truncate text-foreground/80">{c.name || c.phone || "WhatsApp"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* основная область */}
          <div className="min-w-0 flex-1">
            {/* мобильные папки */}
            <div className="flex gap-1.5 overflow-x-auto border-b border-border px-2.5 py-2 lg:hidden">
              {[...inboxAppNav.filter((f) => f.id !== "ai-agent"), ...stages.map((s) => ({ id: `stage:${s.id}`, label: s.name, icon: s.icon || "ri-user-add-line" }))].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => pickFolder(f.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    folder === f.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground",
                  )}
                >
                  <i className={f.icon} aria-hidden />
                  {f.label}
                </button>
              ))}
            </div>

            {/* вкладки и сортировка */}
            <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-1.5">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setTab("chats")}
                  aria-pressed={tab === "chats"}
                  className={cn(
                    "inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn px-2 py-1.5 text-[12px] font-semibold transition",
                    tab === "chats" ? "bg-primary/12 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  {t("Чаты")}
                  <span className="tabular-nums text-[10.5px] opacity-70">{total}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setTab("calls")}
                  aria-pressed={tab === "calls"}
                  className={cn(
                    "inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn px-2 py-1.5 text-[12px] font-semibold transition",
                    tab === "calls" ? "bg-primary/12 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  {t("Звонки")}
                  <span className="tabular-nums text-[10.5px] opacity-70">0</span>
                </button>
              </div>

              <div className="ms-auto flex flex-wrap items-center gap-2">
                {staff || readOnly ? (
                  <span className="relative inline-flex items-center">
                    <select
                      value={assignee}
                      onChange={(e) => {
                        setAssignee(e.target.value);
                        setActiveId(null);
                      }}
                      aria-label={t("Менеджер")}
                      data-testid="inbox-assignee"
                      className={cn(
                        "h-7 cursor-pointer appearance-none rounded-btn border bg-background pe-6 ps-2 text-[11px] font-medium focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20",
                        assignee ? "border-primary text-primary" : "border-border text-muted-foreground",
                      )}
                    >
                      <option value="">{t("Все менеджеры")}</option>
                      <option value="none">
                        {t("Без ответственного")} · {counts.assignees.none ?? 0}
                      </option>
                      {members.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name} · {counts.assignees[m.id] ?? 0}
                        </option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line pointer-events-none absolute end-1 top-1/2 -translate-y-1/2 text-[12px] leading-none text-muted-foreground" aria-hidden />
                  </span>
                ) : null}
                <span className="relative inline-flex items-center">
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    aria-label={t("Сортировка диалогов")}
                    className="h-7 cursor-pointer appearance-none rounded-btn border border-border bg-background pe-6 ps-2 text-[11px] font-medium text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    {inboxAppSorts.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line pointer-events-none absolute end-1 top-1/2 -translate-y-1/2 text-[12px] leading-none text-muted-foreground" aria-hidden />
                </span>

                <span className="inline-flex items-center gap-1.5">
                  <span className="whitespace-nowrap text-[10.5px] font-medium text-muted-foreground">{t("Без ответа")}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={unreplied}
                    aria-label={t("Показать только диалоги без ответа")}
                    onClick={() => {
                      setUnreplied((v) => !v);
                      setTab("chats");
                    }}
                    className={cn("relative h-5 w-9 flex-none cursor-pointer rounded-full border transition", unreplied ? "border-primary bg-primary" : "border-border bg-foreground/10")}
                  >
                    <span className={cn("absolute top-1/2 size-3.5 -translate-y-1/2 rounded-full bg-card transition-all", unreplied ? "start-[1.15rem]" : "start-[0.15rem]")} aria-hidden />
                  </button>
                  {counts.waiting > 0 ? <span className="tabular-nums text-[10.5px] text-amber-600">{counts.waiting}</span> : null}
                </span>

                <button
                  type="button"
                  onClick={() => setSearchOpen((v) => !v)}
                  aria-label={t("Поиск по диалогам")}
                  title={t("Поиск по диалогам")}
                  className={cn("grid size-7 cursor-pointer place-items-center rounded-btn border border-border transition hover:text-foreground", searchOpen ? "text-primary" : "text-muted-foreground")}
                >
                  <Search className="size-3.5" aria-hidden />
                </button>
              </div>
            </div>

            {searchOpen ? (
              <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("Поиск по имени, компании, телефону или тексту последнего сообщения")}
                  className="h-8 w-full box-border rounded-btn border border-border bg-background px-2.5 text-[11.5px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                />
                {query ? (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="inline-flex h-8 flex-none cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn border border-border px-2 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
                  >
                    <X className="size-3" aria-hidden />
                    {t("Сбросить")}
                  </button>
                ) : null}
              </div>
            ) : null}

            {/* содержимое */}
            <div className="min-h-[25rem]">
              {tab === "calls" ? (
                <EmptyState empty={EMPTY["incoming-calls"]} onAction={() => jump("catalog")} />
              ) : loading && list.length === 0 ? (
                <div className="flex min-h-[25rem] items-center justify-center text-muted-foreground">
                  <Loader2 className="size-5 animate-spin" aria-hidden />
                </div>
              ) : list.length === 0 && !active ? (
                <EmptyState
                  empty={q || unreplied ? { title: t("Ничего не нашлось"), text: t("Попробуйте другое слово или снимите фильтр «Без ответа»."), action: t("Сбросить поиск"), target: "reset" } : empty}
                  extra={!q && !unreplied && connected.length === 0 ? t("Подключите WhatsApp — диалоги появятся здесь сами") : undefined}
                  onAction={() => {
                    if (q || unreplied) {
                      setQuery("");
                      setUnreplied(false);
                    } else jump(empty.target);
                  }}
                />
              ) : (
                <div className="grid lg:grid-cols-[17.5rem_minmax(0,1fr)]">
                  <ul className={cn("max-h-[34rem] overflow-y-auto border-border lg:border-e", active && "hidden lg:block")}>
                    {list.map((d) => {
                      const on = d.id === activeId;
                      return (
                        <li key={d.id}>
                          <button
                            type="button"
                            onClick={() => setActiveId(d.id)}
                            className={cn("flex w-full cursor-pointer items-start gap-2.5 border-b border-border/60 px-3 py-2.5 text-start transition hover:bg-muted/60", on && "bg-muted")}
                          >
                            <span className="relative mt-0.5 flex-none">
                              <span className="grid size-8 place-items-center rounded-full bg-primary/15 text-[10.5px] font-bold text-primary" aria-hidden>
                                {initials(d.name)}
                              </span>
                              <span className="absolute -bottom-1 -end-1 grid size-4 place-items-center rounded-full bg-card text-[9px] leading-none text-emerald-500 ring-1 ring-border" aria-hidden>
                                <i className={channelIcon(d.channel)} />
                              </span>
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-2">
                                <span className={cn("truncate text-[12px]", d.unread > 0 ? "font-bold" : "font-semibold")}>{d.name}</span>
                                <span className="ms-auto whitespace-nowrap text-[9.5px] text-muted-foreground">{listTime(d.lastMessageAt)}</span>
                              </span>
                              <span className="mt-0.5 flex items-center gap-1.5">
                                <span className={cn("truncate text-[11px]", d.unread > 0 ? "text-foreground/90" : "text-muted-foreground")}>
                                  {d.lastMessageKind === "out" ? <span className="text-muted-foreground/70">{t("Вы:")} </span> : null}
                                  {d.lastMessageText || t("Нет сообщений")}
                                </span>
                                {d.unread > 0 ? (
                                  <span className="grid min-w-4 flex-none place-items-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground tabular-nums" aria-hidden>
                                    {d.unread}
                                  </span>
                                ) : null}
                              </span>
                              <span className="mt-1 flex flex-wrap items-center gap-1">
                                {d.company ? <span className="rounded-full bg-background/70 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground ring-1 ring-border">{d.company}</span> : null}
                                {d.assignee ? (
                                  <span className="rounded-full bg-background/70 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground ring-1 ring-border">{d.assignee}</span>
                                ) : d.bot ? (
                                  <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-violet-600">
                                    <i className="ri-robot-2-line text-[10px]" aria-hidden />
                                    {t("бот {name}", { name: d.bot })}
                                  </span>
                                ) : (
                                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">{t("не назначен")}</span>
                                )}
                                {d.waiting && d.status !== "closed" ? (
                                  overdue(d) ? (
                                    <span className="rounded-full bg-red-500/15 px-1.5 py-0.5 text-[9px] font-semibold text-red-500" title={t("Без ответа дольше, чем задано в правилах диалогов")}>
                                      {t("Без ответа {n} ч", { n: Math.floor((Date.now() - new Date(d.lastMessageAt as string).getTime()) / 3600_000) })}
                                    </span>
                                  ) : (
                                    <span className="rounded-full bg-amber-400/15 px-1.5 py-0.5 text-[9px] font-semibold text-amber-600">{t("Ждёт ответа")}</span>
                                  )
                                ) : null}
                                {d.category ? <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-medium text-primary">{d.category}</span> : null}
                                {d.status === "closed" && folder !== "closed" ? <span className="rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">{t("закрыт")}</span> : null}
                              </span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                    {list.length < total ? (
                      <li>
                        <button type="button" onClick={() => void loadList(true)} className="w-full cursor-pointer px-3 py-2.5 text-center text-[11px] font-semibold text-primary hover:bg-muted/60">
                          {t("Показать ещё ({n})", { n: total - list.length })}
                        </button>
                      </li>
                    ) : null}
                  </ul>

                  <div className={cn("min-w-0", !active && "hidden lg:block")}>
                    {active ? (
                      <ChatPane
                        conversation={active}
                        me={me}
                        members={members}
                        snippets={snippets}
                        user={user ? { name: user.name || user.email, email: user.email } : null}
                        staff={Boolean(staff)}
                        readOnly={Boolean(readOnly)}
                        busy={busy}
                        onSend={sendMessage}
                        onUpload={uploadFile}
                        onForward={forwardMessage}
                        onAssign={assign}
                        onStatus={setStatus}
                        onCategory={setCategory}
                        rules={rules}
                        onBack={() => setActiveId(null)}
                        onLoadMore={loadMore}
                      />
                    ) : (
                      <div className="flex h-full min-h-[22rem] flex-col items-center justify-center gap-1.5 px-4 text-center">
                        <span className="grid size-10 place-items-center rounded-full border border-dashed border-border text-muted-foreground/70" aria-hidden>
                          <i className="ri-chat-3-line text-[16px] leading-none" />
                        </span>
                        <p className="text-[12px] font-semibold">{t("Выберите диалог")}</p>
                        <p className="max-w-[15rem] text-[10.5px] leading-relaxed text-muted-foreground">
                          {t("Слева {n} диалогов в папке «{folder}».", { n: total, folder: folderLabel })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* подсказки */}
        <div className="grid gap-2 border-t border-border p-3.5 sm:grid-cols-3">
          {inboxAppHints.map((h) => (
            <div key={h.title} className="rounded-btn border border-border bg-background/40 p-2.5">
              <p className="flex items-center gap-1.5 text-[11.5px] font-semibold">
                <i className={cn(h.icon, "text-[13px] leading-none text-primary")} aria-hidden />
                {h.title}
              </p>
              <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{h.text}</p>
            </div>
          ))}
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}

        {/* ── окна: новый диалог и AI-агент ── */}
        {modal ? (
          <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/65 p-3 backdrop-blur-sm sm:items-center md:p-6">
            <div role="dialog" aria-modal="true" aria-labelledby="inbox-modal-title" className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-card border border-border bg-card p-5 shadow-lift">
              <button
                type="button"
                onClick={() => (modal === "new" ? closeNew() : setModal(null))}
                aria-label={t("Закрыть окно")}
                className="absolute end-3 top-3 grid size-8 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" aria-hidden />
              </button>

              {modal === "new" ? (
                <>
                  <div className="flex items-start gap-3 pe-8">
                    <span className="grid size-11 flex-none place-items-center rounded-btn bg-primary/10 text-2xl leading-none text-primary" aria-hidden>
                      <i className="ri-edit-line" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("Написать первым")}</p>
                      <h3 id="inbox-modal-title" className="mt-1 text-lg font-semibold leading-snug">
                        {t("Новый диалог")}
                      </h3>
                    </div>
                  </div>
                  {writable.length === 0 ? (
                    <p className="mt-3 text-[11.5px] leading-relaxed text-muted-foreground">{t("Нет подключённого WhatsApp или почты — сначала подключите канал в разделе «Каналы».")}</p>
                  ) : (
                    <>
                      <label className="mt-4 block">
                        <span className="text-[11.5px] font-semibold">{newIsMail ? t("С какого ящика") : t("С какого номера")}</span>
                        <select
                          value={newTarget?.id ?? ""}
                          onChange={(e) => setNewChannel(e.target.value)}
                          data-testid="new-dialog-channel"
                          className="mt-1.5 h-9 w-full box-border rounded-btn border border-border bg-background px-2.5 text-[12.5px] text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                        >
                          {writable.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.kind === "email" ? `${t("Почта")}: ${c.name}` : `WhatsApp: ${c.name || c.phone}${c.phone && c.name ? ` · ${c.phone}` : ""}`}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="mt-3 block">
                        <span className="text-[11.5px] font-semibold">{newIsMail ? t("Адрес почты клиента") : t("Телефон клиента")}</span>
                        <input
                          value={newPhone}
                          onChange={(e) => setNewPhone(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") void createNew();
                          }}
                          inputMode={newIsMail ? "email" : "tel"}
                          placeholder={newIsMail ? "name@company.com" : "+971 50 123 4567"}
                          data-testid="new-dialog-to"
                          className="mt-1.5 h-9 w-full box-border rounded-btn border border-border bg-background px-2.5 text-[12.5px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                        />
                      </label>
                      {newIsMail ? (
                        <label className="mt-3 block">
                          <span className="text-[11.5px] font-semibold">{t("Тема письма")}</span>
                          <input
                            value={newSubject}
                            onChange={(e) => setNewSubject(e.target.value)}
                            data-testid="new-dialog-subject"
                            className="mt-1.5 h-9 w-full box-border rounded-btn border border-border bg-background px-2.5 text-[12.5px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                          />
                        </label>
                      ) : null}
                      <label className="mt-3 block">
                        <span className="text-[11.5px] font-semibold">{newIsMail ? t("Текст письма") : t("Сообщение")}</span>
                        <textarea
                          value={newText}
                          onChange={(e) => setNewText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                              e.preventDefault();
                              void createNew();
                            }
                          }}
                          rows={newIsMail ? 6 : 3}
                          placeholder={newIsMail ? t("Текст письма…") : t("Первое сообщение — можно оставить пустым")}
                          data-testid="new-dialog-text"
                          className="mt-1.5 w-full box-border resize-y rounded-btn border border-border bg-background px-2.5 py-2 text-[12.5px] leading-relaxed text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                        />
                      </label>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => newFileInput.current?.click()}
                          title={t("Отправить файлы: фото, видео, голосовое или документ любого формата, до {n} МБ каждый; можно несколько сразу", { n: MAX_UPLOAD_MB })}
                          className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-btn border border-border px-2.5 text-[11.5px] font-semibold text-muted-foreground transition hover:bg-muted hover:text-foreground"
                        >
                          <Paperclip className="size-3.5" aria-hidden />
                          {t("Прикрепить файл")}
                        </button>
                        <input
                          ref={newFileInput}
                          type="file"
                          multiple
                          className="hidden"
                          data-testid="new-dialog-file"
                          onChange={(e) => {
                            const picked = Array.from(e.target.files ?? []);
                            if (picked.length) setNewFiles((prev) => [...prev, ...picked].slice(0, 10));
                            e.target.value = "";
                          }}
                        />
                        {newFiles.map((f, i) => (
                          <span key={`${f.name}-${i}`} className="inline-flex h-8 max-w-full items-center gap-1.5 rounded-btn border border-border bg-background px-2.5 text-[11.5px]">
                            <span className="truncate">{f.name}</span>
                            <button
                              type="button"
                              onClick={() => setNewFiles((prev) => prev.filter((_, k) => k !== i))}
                              aria-label={t("Убрать файл")}
                              className="grid size-5 cursor-pointer place-items-center rounded text-muted-foreground hover:text-foreground"
                            >
                              <X className="size-3" aria-hidden />
                            </button>
                          </span>
                        ))}
                      </div>
                      <p className="mt-2 text-[10.5px] leading-relaxed text-muted-foreground">
                        {newIsMail
                          ? t("Письмо уйдёт от имени выбранного ящика, файлы — вложениями. Если контакт с таким адресом уже есть, диалог привяжется к нему.")
                          : t("Если контакт с таким номером уже есть, диалог привяжется к нему. Писать первым можно, но массовые рассылки с обычного номера WhatsApp блокирует.")}
                      </p>
                    </>
                  )}
                  <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={() => closeNew()}
                      className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-4 text-[12.5px] font-semibold text-muted-foreground transition hover:text-foreground"
                    >
                      {t("Отмена")}
                    </button>
                    {writable.length ? (
                      <button
                        type="button"
                        onClick={() => void createNew()}
                        disabled={!newValid || newBusy}
                        data-testid="new-dialog-submit"
                        className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {newBusy ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : <i className={cn(newHasMessage ? "ri-send-plane-2-line" : "ri-chat-new-line", "text-[14px] leading-none")} aria-hidden />}
                        {newHasMessage ? t("Отправить") : t("Открыть диалог")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          closeNew(true);
                          jump("channels");
                        }}
                        className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110"
                      >
                        {t("Подключить канал")}
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start gap-3 pe-8">
                    <span className="grid size-11 flex-none place-items-center rounded-btn bg-primary/10 text-2xl leading-none text-primary" aria-hidden>
                      <i className="ri-sparkling-line" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("AI-агент")}</p>
                      <h3 id="inbox-modal-title" className="mt-1 text-lg font-semibold leading-snug">
                        {t("Создать AI-агента")}
                      </h3>
                    </div>
                  </div>

                  <p className="mt-3 text-[11.5px] leading-relaxed text-muted-foreground">
                    {t("Агент отвечает первым, пока продавцы заняты, и передаёт человеку сложные вопросы вместе с контекстом диалога.")}
                  </p>

                  <div className="mt-4 flex flex-col gap-2">
                    {inboxAppAgentSteps.map((s) => (
                      <div key={s.title} className="flex items-start gap-2.5 rounded-btn border border-border bg-background/50 p-2.5">
                        <span className="grid size-7 flex-none place-items-center rounded-btn bg-primary/12 text-[13px] leading-none text-primary" aria-hidden>
                          <i className={s.icon} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-[12px] font-semibold">{s.title}</p>
                          <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">{s.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("Чем займётся агент")}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {inboxAppAgentRoles.map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setAgentRole(r)}
                        aria-pressed={agentRole === r}
                        className={cn(
                          "cursor-pointer whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                          agentRole === r ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {r}
                      </button>
                    ))}
                  </div>

                  <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={() => setModal(null)}
                      className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-4 text-[12.5px] font-semibold text-muted-foreground transition hover:text-foreground"
                    >
                      {t("Позже")}
                    </button>
                    <button
                      type="button"
                      onClick={createAgent}
                      className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110"
                    >
                      <i className="ri-sparkling-line text-[14px] leading-none" aria-hidden />
                      {t("Создать агента")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>

      <p className="mt-4 flex items-start gap-2 text-[11.5px] leading-relaxed text-muted-foreground">
        <ChevronDown className="mt-0.5 size-3.5 flex-none text-primary" aria-hidden />
        {t("Папки входящих и жизненный цикл — один список: этапы продаж сразу становятся папками, где лежат диалоги нужных контактов.")}
      </p>
    </Band>
  );
}

export default InboxAppDemo;
